import { RasterPixels } from './types';

// Longest-side cap for the analysis raster. Small enough to stay cheap, large
// enough to preserve gradients / fine detail. Mirrors the perf approach in
// detectImageTransparency.ts (which samples at 100px).
const MAX_ANALYSIS_SIZE = 512;

/**
 * Loads an image URL and returns its downscaled RGBA pixels for analysis, or
 * `null` on CORS/decode errors. Browser-only (uses canvas); not used in unit
 * tests, which exercise the pure analyzer directly.
 */
function loadRasterFromUrl(imageUrl: string, maxSize = MAX_ANALYSIS_SIZE): Promise<RasterPixels | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        if (!img.width || !img.height) {
          resolve(null);
          return;
        }

        const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(img.width * scale));
        canvas.height = Math.max(1, Math.floor(img.height * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        resolve({
          data: imageData.data,
          width: imageData.width,
          height: imageData.height,
        });
      } catch {
        // CORS-tainted canvas or other failure — skip this image.
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
}

export default loadRasterFromUrl;
